import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  Course,
  CourseWithDetails,
  CourseLevel,
  CourseStatus,
} from "@learn-me/types";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  Badge,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { categoriesApi } from "@/features/categories/api/categoriesApi";
import { ReferenceDocsPanel } from "@/features/admin/components/ReferenceDocsPanel";
import { GenerateChaptersModal } from "@/features/admin/components/GenerateChaptersModal";
import { PublishValidation } from "@/features/admin/components/PublishValidation";
import { AIToolsPanel } from "@/features/admin/components/AIToolsPanel";
import { SortableChapterList } from "@/components/SortableChapterList";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  ArrowLeft,
  Plus,
  AlertCircle,
  CheckCircle2,
  Image,
  Sparkles,
  Wand2,
  Loader2,
  Eye,
  Tag,
  Check,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { normalizeImageUrl } from "@/lib/imageUrl";

interface CourseFormData {
  title: string;
  slug: string;
  description: string;
  thumbnailUrl?: string;
  listPrice?: number | null;
  salePrice?: number | null;
  hasDiscount?: boolean;
  currency: string;
  level?: CourseLevel;
  isFree: boolean;
  status: CourseStatus;
  /**
   * Horas académicas del curso, solo para la interfaz. Se envía al API como
   * `duration` en minutos, que es la unidad que guarda la base de datos y la
   * que el certificado divide entre 60 para imprimir las horas.
   */
  academicHours?: number | null;
}

export default function CourseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [showGenerateChaptersModal, setShowGenerateChaptersModal] =
    useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Reorder chapters mutation with optimistic updates
  const reorderMutation = useMutation({
    mutationFn: (chapters: Array<{ id: string; position: number }>) =>
      adminApi.reorderChapters(id!, chapters),
    onMutate: async (newOrder) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["course-edit", id] });

      // Snapshot previous value for rollback
      const previousCourse = queryClient.getQueryData<CourseWithDetails>([
        "course-edit",
        id,
      ]);

      // Optimistically update the cache
      queryClient.setQueryData<CourseWithDetails>(
        ["course-edit", id],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            chapters: newOrder.map((item) => {
              const existingChapter = old.chapters.find(
                (c) => c.id === item.id,
              );
              return existingChapter
                ? { ...existingChapter, position: item.position }
                : existingChapter!;
            }),
          };
        },
      );

      return { previousCourse };
    },
    onError: (_err, _newOrder, context) => {
      // Rollback on error
      if (context?.previousCourse) {
        queryClient.setQueryData(["course-edit", id], context.previousCourse);
      }
      toast.error("Error al reordenar los capítulos");
    },
    onSuccess: () => {
      toast.success("Orden actualizado");
    },
    onSettled: () => {
      // Always refetch to sync with server
      queryClient.invalidateQueries({ queryKey: ["course-edit", id] });
    },
  });

  const handleReorderChapters = useCallback(
    async (chapters: Array<{ id: string; position: number }>) => {
      await reorderMutation.mutateAsync(chapters);
    },
    [reorderMutation],
  );

  // Eliminar capítulo
  const [chapterToDelete, setChapterToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const deleteChapterMutation = useMutation({
    mutationFn: (chapterId: string) => adminApi.deleteChapter(chapterId),
    onSuccess: () => {
      toast.success("Capítulo eliminado");
      queryClient.invalidateQueries({ queryKey: ["course-edit", id] });
    },
    onError: () => {
      toast.error("Error al eliminar el capítulo");
    },
    onSettled: () => {
      setChapterToDelete(null);
    },
  });

  const { data: course, isLoading } = useQuery({
    queryKey: ["course-edit", id],
    queryFn: () => adminApi.getCourse(id!),
    enabled: isEditing,
  });

  // Fetch all available categories
  const { data: allCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: categoriesApi.list,
  });

  // Fetch course categories when editing
  const { data: courseCategories } = useQuery({
    queryKey: ["course-categories", id],
    queryFn: () => adminApi.getCourseCategories(id!),
    enabled: isEditing,
  });

  // Update course categories mutation
  const updateCategoriesMutation = useMutation({
    mutationFn: (categoryIds: string[]) =>
      adminApi.updateCourseCategories(id!, categoryIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-categories", id] });
      toast.success("Categorías actualizadas");
    },
    onError: () => {
      toast.error("Error al actualizar las categorías");
    },
  });

  // Sync selected categories with fetched data
  useEffect(() => {
    if (courseCategories) {
      setSelectedCategoryIds(courseCategories.map((c) => c.id));
    }
  }, [courseCategories]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
  } = useForm<CourseFormData>({
    defaultValues: {
      currency: "USD",
      isFree: false,
      status: "DRAFT",
      listPrice: null,
      salePrice: null,
      hasDiscount: false,
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        title: course.title,
        slug: course.slug,
        description: course.description || "",
        thumbnailUrl: course.thumbnailUrl || "",
        listPrice: course.listPrice ? Number(course.listPrice) : null,
        salePrice: course.salePrice ? Number(course.salePrice) : null,
        hasDiscount: course.hasDiscount ?? false,
        currency: course.currency,
        level: course.level || undefined,
        isFree: course.isFree,
        status: course.status,
        academicHours: course.duration ? course.duration / 60 : null,
      });
    }
  }, [course, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<Course>) => adminApi.createCourse(data),
    onSuccess: (newCourse) => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Curso creado");
      navigate(`/admin/courses/${newCourse.id}`);
    },
    onError: (
      error: Error & { response?: { data?: { error?: { message?: string } } } },
    ) => {
      const message =
        error.response?.data?.error?.message || "Error al crear el curso";
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Course>) => adminApi.updateCourse(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      queryClient.invalidateQueries({ queryKey: ["course-edit", id] });
      toast.success("Curso actualizado");
    },
    onError: (
      error: Error & { response?: { data?: { error?: { message?: string } } } },
    ) => {
      const message =
        error.response?.data?.error?.message || "Error al actualizar el curso";
      toast.error(message);
    },
  });

  const onSubmit = (data: CourseFormData) => {
    // Validar que cursos de pago tengan precio
    if (!data.isFree && (!data.salePrice || data.salePrice <= 0)) {
      setError("salePrice", {
        type: "manual",
        message: "El precio es requerido para cursos de pago",
      });
      return;
    }

    // Validar coherencia del descuento
    if (
      !data.isFree &&
      data.listPrice != null &&
      data.salePrice != null &&
      data.listPrice <= data.salePrice
    ) {
      setError("listPrice", {
        type: "manual",
        message:
          "El precio original debe ser mayor al precio de venta para aplicar descuento",
      });
      return;
    }

    // hasDiscount se deriva: hay descuento si existe listPrice > salePrice
    const hasDiscount =
      !data.isFree &&
      data.listPrice != null &&
      data.salePrice != null &&
      data.listPrice > data.salePrice;

    // Preparar payload con precio limpio. Las horas académicas viajan al API
    // como duración en minutos.
    const { academicHours, ...courseData } = data;
    const payload = {
      ...courseData,
      salePrice: data.isFree ? null : data.salePrice,
      listPrice: data.isFree ? null : hasDiscount ? data.listPrice : null,
      hasDiscount,
      duration: academicHours ? Math.round(academicHours * 60) : null,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      // Al crear, no enviamos slug ni status (el backend los genera/asigna)
      const { slug: _slug, status: _status, ...createData } = payload;
      // Limpiar level si está vacío
      if (!createData.level || createData.level === ("" as CourseLevel)) {
        delete createData.level;
      }
      createMutation.mutate(createData);
    }
  };

  const isFree = watch("isFree");
  const title = watch("title");

  // Toggle category selection
  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  // Save categories
  const handleSaveCategories = () => {
    updateCategoriesMutation.mutate(selectedCategoryIds);
  };

  // Check if categories have changed
  const categoriesChanged =
    courseCategories &&
    (selectedCategoryIds.length !== courseCategories.length ||
      selectedCategoryIds.some(
        (id) => !courseCategories.find((c) => c.id === id),
      ));

  // Improve description mutation
  const improveDescriptionMutation = useMutation({
    mutationFn: () => adminApi.improveCourseDescription(id!),
    onSuccess: (data) => {
      setValue("description", data.description);
      toast.success("Descripción mejorada. Revisa y guarda los cambios.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al mejorar la descripción");
    },
  });

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditing && title) {
      const slug = title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setValue("slug", slug);
    }
  }, [title, isEditing, setValue]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-1/4" />
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/courses">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </Link>
        <h1 className="text-3xl font-bold">
          {isEditing ? "Editar Curso" : "Nuevo Curso"}
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Informacion del Curso</h2>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Titulo */}
                <div>
                  <Input
                    label="Titulo *"
                    placeholder="Ej: Introduccion a React"
                    error={errors.title?.message}
                    {...register("title", {
                      required: "El titulo es requerido",
                      minLength: {
                        value: 3,
                        message: "El titulo debe tener al menos 3 caracteres",
                      },
                      maxLength: {
                        value: 200,
                        message: "El titulo no puede exceder 200 caracteres",
                      },
                    })}
                  />
                  {title && !errors.title && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      {title.length}/200 caracteres
                    </p>
                  )}
                </div>

                {/* Slug - solo visible en edicion */}
                {isEditing && (
                  <div>
                    <Input
                      label="Slug (URL)"
                      placeholder="introduccion-a-react"
                      error={errors.slug?.message}
                      disabled
                      {...register("slug")}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      El slug se genera automaticamente y no puede modificarse
                    </p>
                  </div>
                )}

                {/* Descripcion */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">
                      Descripcion *
                    </label>
                    {isEditing && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => improveDescriptionMutation.mutate()}
                        disabled={improveDescriptionMutation.isPending}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      >
                        {improveDescriptionMutation.isPending ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Mejorando...
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-3 w-3 mr-1" />
                            Mejorar con IA
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <textarea
                    className={`w-full px-3 py-2 border rounded-lg min-h-[120px] transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.description
                        ? "border-red-500 focus:ring-red-500"
                        : "border-secondary-300 hover:border-secondary-400"
                    }`}
                    placeholder="Describe el contenido del curso. Explica que aprendera el estudiante, los temas principales y los requisitos previos..."
                    {...register("description", {
                      required: "La descripcion es requerida",
                      minLength: {
                        value: 10,
                        message:
                          "La descripcion debe tener al menos 10 caracteres",
                      },
                      maxLength: {
                        value: 5000,
                        message:
                          "La descripcion no puede exceder 5000 caracteres",
                      },
                    })}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.description ? (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.description.message}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Minimo 10 caracteres
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {watch("description")?.length || 0}/5000
                    </p>
                  </div>
                </div>

                {/* Thumbnail URL con preview */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    URL de Imagen (Thumbnail)
                  </label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Input
                        placeholder="https://ejemplo.com/imagen.jpg"
                        error={errors.thumbnailUrl?.message}
                        {...register("thumbnailUrl", {
                          pattern: {
                            value: /^(https?:\/\/.+)?$/i,
                            message:
                              "Ingresa una URL válida que comience con http:// o https://",
                          },
                        })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        URL pública de imagen. Acepta enlaces de Google Drive,
                        Cloudinary, Firebase, etc. (con o sin extensión).
                      </p>
                    </div>
                    {watch("thumbnailUrl") && !errors.thumbnailUrl && (
                      <div className="w-20 h-20 rounded-lg border overflow-hidden flex-shrink-0">
                        <img
                          src={normalizeImageUrl(watch("thumbnailUrl")) ?? ""}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                    )}
                    {!watch("thumbnailUrl") && (
                      <div className="w-20 h-20 rounded-lg border border-dashed flex items-center justify-center flex-shrink-0 bg-muted/50">
                        <Image className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Nivel, horas académicas y Moneda */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nivel
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                      {...register("level")}
                    >
                      <option value="">Seleccionar nivel...</option>
                      <option value="BEGINNER">Principiante</option>
                      <option value="INTERMEDIATE">Intermedio</option>
                      <option value="ADVANCED">Avanzado</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ayuda a los estudiantes a encontrar cursos adecuados
                    </p>
                  </div>

                  <div>
                    <Input
                      type="number"
                      label="Horas académicas"
                      placeholder="40"
                      step="1"
                      min="0"
                      error={errors.academicHours?.message}
                      {...register("academicHours", {
                        setValueAs: (v) =>
                          v === "" || v === null || isNaN(Number(v))
                            ? null
                            : Number(v),
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Se imprime en el certificado del estudiante
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Moneda
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                      {...register("currency")}
                    >
                      <option value="USD">USD - Dolar Estadounidense</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="MXN">MXN - Peso Mexicano</option>
                    </select>
                  </div>
                </div>

                {/* Precio y Gratuito */}
                <div className="p-4 bg-muted/30 rounded-lg border space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                          {...register("isFree")}
                        />
                        <span className="font-medium">Curso Gratuito</span>
                      </label>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        Los cursos gratuitos no requieren pago para inscribirse
                      </p>
                    </div>
                  </div>

                  {!isFree && (
                    <div className="pt-2 border-t space-y-4">
                      <Input
                        type="number"
                        label="Precio de venta *"
                        placeholder="99.99"
                        step="0.01"
                        min="0"
                        error={errors.salePrice?.message}
                        {...register("salePrice", {
                          valueAsNumber: true,
                          setValueAs: (v) =>
                            v === "" || v === null || isNaN(Number(v))
                              ? null
                              : Number(v),
                        })}
                        onChange={(e) => {
                          const value = e.target.value;
                          setValue(
                            "salePrice",
                            value === "" ? null : Number(value),
                          );
                          if (errors.salePrice) {
                            clearErrors("salePrice");
                          }
                        }}
                      />

                      <div>
                        <Input
                          type="number"
                          label="Precio original (para mostrar descuento)"
                          placeholder="149.99"
                          step="0.01"
                          min="0"
                          error={errors.listPrice?.message}
                          {...register("listPrice", {
                            valueAsNumber: true,
                            setValueAs: (v) =>
                              v === "" || v === null || isNaN(Number(v))
                                ? null
                                : Number(v),
                          })}
                          onChange={(e) => {
                            const value = e.target.value;
                            setValue(
                              "listPrice",
                              value === "" ? null : Number(value),
                            );
                            if (errors.listPrice) {
                              clearErrors("listPrice");
                            }
                          }}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Opcional. Si lo defines mayor al precio de venta, se
                          mostrará tachado con la etiqueta de descuento.
                        </p>
                        {(() => {
                          const lp = watch("listPrice");
                          const sp = watch("salePrice");
                          if (lp != null && sp != null && lp > sp && sp > 0) {
                            const pct = Math.round(((lp - sp) / lp) * 100);
                            return (
                              <p className="text-xs font-medium text-green-600 mt-1">
                                Descuento aplicado: {pct}% OFF
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Estado - solo en edicion */}
                {isEditing && (
                  <div className="p-4 bg-muted/30 rounded-lg border">
                    <label className="block text-sm font-medium mb-2">
                      Estado del Curso
                    </label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          watch("status") === "DRAFT"
                            ? "border-primary-500 bg-primary-50"
                            : "border-secondary-300 hover:border-secondary-400"
                        }`}
                      >
                        <input
                          type="radio"
                          value="DRAFT"
                          className="w-4 h-4"
                          {...register("status")}
                        />
                        <div>
                          <p className="font-medium">Borrador</p>
                          <p className="text-xs text-muted-foreground">
                            No visible para estudiantes
                          </p>
                        </div>
                      </label>
                      <label
                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          watch("status") === "PUBLISHED"
                            ? "border-green-500 bg-green-50"
                            : "border-secondary-300 hover:border-secondary-400"
                        }`}
                      >
                        <input
                          type="radio"
                          value="PUBLISHED"
                          className="w-4 h-4"
                          {...register("status")}
                        />
                        <div>
                          <p className="font-medium">Publicado</p>
                          <p className="text-xs text-muted-foreground">
                            Visible en el catalogo
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Botones de accion */}
                <div className="flex gap-4 pt-4 border-t">
                  <Button
                    type="submit"
                    isLoading={
                      createMutation.isPending || updateMutation.isPending
                    }
                    className="min-w-[140px]"
                  >
                    {isEditing ? "Guardar Cambios" : "Crear Curso"}
                  </Button>
                  <Link to="/admin/courses">
                    <Button type="button" variant="outline">
                      Cancelar
                    </Button>
                  </Link>
                </div>

                {/* Resumen de errores */}
                {Object.keys(errors).length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Por favor corrige los siguientes errores:
                    </p>
                    <ul className="mt-2 text-sm text-red-600 list-disc list-inside space-y-1">
                      {errors.title && <li>{errors.title.message}</li>}
                      {errors.description && (
                        <li>{errors.description.message}</li>
                      )}
                      {errors.thumbnailUrl && (
                        <li>{errors.thumbnailUrl.message}</li>
                      )}
                      {errors.salePrice && <li>{errors.salePrice.message}</li>}
                    </ul>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        {isEditing && (
          <div className="space-y-6">
            {/* Preview Course Button */}
            <Link to={`/admin/courses/${id}/preview`}>
              <Button variant="outline" className="w-full">
                <Eye className="h-4 w-4 mr-2" />
                Vista Previa del Curso
              </Button>
            </Link>

            {/* Categories */}
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Categorías
                </h2>
                {categoriesChanged && (
                  <Button
                    size="sm"
                    onClick={handleSaveCategories}
                    disabled={updateCategoriesMutation.isPending}
                  >
                    {updateCategoriesMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Guardar"
                    )}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {allCategories && allCategories.length > 0 ? (
                  <div className="space-y-2">
                    {allCategories.map((category) => {
                      const isSelected = selectedCategoryIds.includes(
                        category.id,
                      );
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                            isSelected
                              ? "bg-primary-50 border border-primary-200"
                              : "bg-muted/50 hover:bg-muted border border-transparent"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSelected
                                ? "bg-primary-500 text-white"
                                : "border border-secondary-300"
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <span className="flex-1 truncate">
                            {category.name}
                          </span>
                          {category.isMainArea && (
                            <Badge variant="outline" className="text-xs">
                              Principal
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay categorías disponibles.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Chapters */}
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <h2 className="text-lg font-semibold">Capitulos</h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowGenerateChaptersModal(true)}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                  <Link to={`/admin/courses/${id}/chapters/new`}>
                    <Button size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {!course?.chapters?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay capitulos aun.
                  </p>
                ) : (
                  <SortableChapterList
                    chapters={course.chapters}
                    courseId={id!}
                    onReorder={handleReorderChapters}
                    isReordering={reorderMutation.isPending}
                    onDelete={(chapter) =>
                      setChapterToDelete({
                        id: chapter.id,
                        title: chapter.title,
                      })
                    }
                    deletingChapterId={
                      deleteChapterMutation.isPending
                        ? deleteChapterMutation.variables
                        : null
                    }
                  />
                )}
              </CardContent>
            </Card>

            {/* Final Exam */}
            <Link to={`/admin/courses/${id}/final-exam`}>
              <Button variant="outline" className="w-full">
                <FileText className="h-4 w-4 mr-2" />
                Examen Final
              </Button>
            </Link>

            {/* Publish Validation */}
            <PublishValidation courseId={id!} />

            {/* AI Tools */}
            <AIToolsPanel courseId={id!} />

            {/* Reference Documents */}
            <ReferenceDocsPanel courseId={id!} />
          </div>
        )}
      </div>

      {/* Generate Chapters Modal */}
      {isEditing && (
        <GenerateChaptersModal
          isOpen={showGenerateChaptersModal}
          onClose={() => setShowGenerateChaptersModal(false)}
          courseId={id!}
          existingChaptersCount={course?.chapters?.length || 0}
          onChaptersGenerated={() => {
            queryClient.invalidateQueries({ queryKey: ["course-edit", id] });
          }}
        />
      )}

      {/* Confirmación de eliminación de capítulo */}
      <ConfirmDialog
        isOpen={chapterToDelete !== null}
        onClose={() => setChapterToDelete(null)}
        onConfirm={() => {
          if (chapterToDelete) {
            deleteChapterMutation.mutate(chapterToDelete.id);
          }
        }}
        title="Eliminar capítulo"
        description={`¿Seguro que deseas eliminar "${chapterToDelete?.title ?? ""}"? Esta acción no se puede deshacer y se eliminará todo su contenido.`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteChapterMutation.isPending}
      />
    </div>
  );
}
